import { Input } from '@/components/ui/input';
import { countryCodes } from '@/lib/country-codes';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, Search } from 'lucide-react';
import * as React from 'react';

interface CountryCodeSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

export function CountryCodeSelect({
  value,
  onValueChange,
  className,
  disabled,
}: CountryCodeSelectProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const selectedCountry = countryCodes.find(
    (country) => country.dialCode === value
  );

  // Simple filtering function that actually works
  const getFilteredCountries = () => {
    if (!searchQuery.trim()) {
      return countryCodes.sort((a, b) => a.name.localeCompare(b.name));
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = countryCodes.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(query)
    );

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredCountries = getFilteredCountries();

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleCountrySelect = (dialCode: string) => {
    onValueChange(dialCode);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        className={cn(
          'h-12 px-2 py-3 bg-black/30 backdrop-blur-sm border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent rounded-l-xl rounded-r-none border-r-0 w-24 flex-shrink-0 flex items-center justify-between',
          className
        )}>
        <div className="flex items-center gap-1">
          {selectedCountry ? (
            <>
              <span className="text-base">{selectedCountry.flag}</span>
              <span className="text-sm font-medium">
                {selectedCountry.dialCode}
              </span>
            </>
          ) : (
            <span className="text-white/60 text-sm">+1</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-3 w-3 opacity-50 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-black/90 backdrop-blur-md border border-white/30 text-white rounded-lg shadow-lg z-50 w-64 max-h-80">
          {/* Search Input */}
          <div className="p-2 border-b border-white/20">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
              <Input
                ref={searchInputRef}
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 bg-black/50 border-white/30 text-white placeholder-white/60 focus:ring-purple-400 focus:border-purple-400"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country.dialCode)}
                  className="w-full px-3 py-2 text-left text-white hover:bg-purple-500/20 focus:bg-purple-500/20 focus:outline-none cursor-pointer flex items-center gap-3">
                  <span className="text-lg">{country.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{country.name}</div>
                    <div className="text-xs text-white/70">
                      {country.dialCode}
                    </div>
                  </div>
                  {country.dialCode === value && (
                    <Check className="h-4 w-4 text-purple-400" />
                  )}
                </button>
              ))
            ) : (
              <div className="p-4 text-center text-white/60 text-sm">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
